import { useState, useEffect, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Av, Bdg, AiScoreRing, PH } from '../../components/shared';
import { useApp } from '../../context/AppContext';
import * as api from '../../services/api';

export const PageUsers = memo(()=>{
  const { showToast } = useApp();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ current_page:1, last_page:1, total:0 });
  const [removeModal, setRemoveModal] = useState(null); // { id, name, isRequested }

  const fetchMembers = useCallback(async (p = page, search = q) => {
    setLoading(true);
    try {
      const res = await api.getMembers(p, search);
      const paginated = res.data || res;
      const items = paginated.data || paginated;
      setMembers(Array.isArray(items) ? items : []);
      setPagination({
        current_page: paginated.current_page || 1,
        last_page: paginated.last_page || 1,
        total: paginated.total || items.length || 0,
      });
    } catch(e) {
      showToast(e.message || "Failed to load members", "error");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMembers(page, q); }, [page]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); fetchMembers(1, q); }, 400);
    return () => clearTimeout(t);
  }, [q]);

  const handleRemoveConfirm = async () => {
    if (!removeModal) return;
    const { id, isRequested } = removeModal;
    try {
      if (isRequested) {
        await api.disconnectMember(id);
        showToast("Member disconnected from enterprise");
      } else {
        await api.deleteMember(id);
        showToast("Member deleted permanently");
      }
      setRemoveModal(null);
      fetchMembers(page, q);
    } catch(e) {
      showToast(e.message || "Failed to remove member", "error");
    }
  };

  // Build page numbers
  const { current_page, last_page, total } = pagination;
  const pages = [];
  for (let i = 1; i <= last_page; i++) {
    if (i === 1 || i === last_page || (i >= current_page - 2 && i <= current_page + 2)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }

  return (
    <div className="pg fi">
      <PH eyebrow="Team" title="Members" sub={`${total} team members`}
        action={<button className="btn bp" onClick={() => navigate('/team/users/new')}>+ Create User</button>}/>

      <div style={{marginBottom:16}}>
        <input className="inp" style={{maxWidth:300}} placeholder="🔍  Search by name or email…"
          value={q} onChange={e => setQ(e.target.value)}/>
      </div>

      <div className="card-s">
        {loading ? (
          <div style={{padding:40,textAlign:"center",color:"var(--t3)",fontSize:13}}>Loading members...</div>
        ) : members.length === 0 ? (
          <div style={{padding:40,textAlign:"center",color:"var(--t3)",fontSize:13}}>
            {q ? "No members match your search" : "No members found"}
          </div>
        ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>Member</th>
              <th>Card UUID</th>
              <th>Position</th>
              <th>Leads</th>
              <th>AI Score</th>
              <th>Subteam</th>
              <th>Status</th>
              <th style={{textAlign:"right",paddingRight:14}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.map(m => (
              <tr key={m.id}>
                <td>
                  <div style={{display:"flex",alignItems:"center",gap:9}}>
                    {m.photo ? (
                      <img src={m.photo} alt={m.name} style={{width:32,height:32,borderRadius:10,objectFit:"cover"}}/>
                    ) : (
                      <Av name={m.name ? m.name.slice(0,2).toUpperCase() : "??"} grad={m.profile_color || m.grad || "linear-gradient(150deg,#64748b,#059669)"} size={32} online={m.online}/>
                    )}
                    <div>
                      <div style={{fontWeight:570,fontSize:13}}>{m.name || "—"}</div>
                      <div style={{fontSize:11,color:"var(--t3)"}}>{m.email}</div>
                    </div>
                  </div>
                </td>
                <td>
                  {m.card_uuid ? (
                    <div style={{display:"flex",alignItems:"center",gap:6,fontFamily:"monospace"}}>
                      <div style={{fontSize:11,color:"#94a3b8",background:"rgba(100,116,139,.08)",padding:"3px 8px",borderRadius:5,letterSpacing:".04em",maxWidth:120,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        {m.card_uuid}
                      </div>
                      <button onClick={e => {e.stopPropagation(); navigator.clipboard?.writeText(m.card_uuid); showToast("UUID copied successfully!");}}
                        style={{width:24,height:24,border:"1px solid rgba(100,116,139,.18)",background:"rgba(100,116,139,.06)",borderRadius:6,cursor:"pointer",fontSize:12,color:"#64748b",padding:0,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",transition:"all .14s"}}
                        onMouseEnter={e=>{e.currentTarget.style.background="rgba(100,116,139,.14)";e.currentTarget.style.color="#0f172a";}}
                        onMouseLeave={e=>{e.currentTarget.style.background="rgba(100,116,139,.06)";e.currentTarget.style.color="#64748b";}}
                        title="Copy UUID">⧉</button>
                    </div>
                  ) : (
                    <span style={{color:"var(--t4)"}}>—</span>
                  )}
                </td>
                <td style={{fontSize:13,color:"var(--t2)"}}>{m.job_title || m.position || m.role || "—"}</td>
                <td><b style={{color:"var(--blue)"}}>{m.leads_count ?? m.leads ?? 0}</b></td>
                <td><AiScoreRing score={m.score ?? m.tiks ?? 0}/></td>
                <td style={{fontSize:12,color:"var(--t2)"}}>{m.subteam_names || <span style={{color:"var(--t4)"}}>—</span>}</td>
                <td><Bdg s={m.active ? 'active' : 'pending'}/></td>
                <td>
                  <div style={{display:"flex",gap:5,justifyContent:"flex-end",padding:"0 6px"}}>
                    <button title="Member Analytics"
                      onClick={()=>navigate(`/team/users/analytics?id=${m.id}`)}
                      style={{display:"flex",alignItems:"center",gap:5,padding:"5px 10px",borderRadius:8,border:"1px solid rgba(124,58,237,.22)",background:"rgba(124,58,237,.07)",cursor:"pointer",fontSize:11,fontWeight:600,color:"#7c3aed",fontFamily:"var(--f)",transition:"all .14s",whiteSpace:"nowrap"}}
                      onMouseEnter={e=>{e.currentTarget.style.background="rgba(124,58,237,.15)";}}
                      onMouseLeave={e=>{e.currentTarget.style.background="rgba(124,58,237,.07)";}}>
                      📊 Analytics
                    </button>
                    <button title="Member Leads"
                      onClick={()=>navigate(`/team/users/leads?id=${m.id}`)}
                      style={{display:"flex",alignItems:"center",gap:5,padding:"5px 10px",borderRadius:8,border:"1px solid rgba(5,150,105,.22)",background:"rgba(5,150,105,.07)",cursor:"pointer",fontSize:11,fontWeight:600,color:"#059669",fontFamily:"var(--f)",transition:"all .14s",whiteSpace:"nowrap"}}
                      onMouseEnter={e=>{e.currentTarget.style.background="rgba(5,150,105,.15)";}}
                      onMouseLeave={e=>{e.currentTarget.style.background="rgba(5,150,105,.07)";}}>
                      🎯 Leads
                    </button>
                    <button title="Edit Profile"
                      onClick={()=>navigate(`/team/users/edit?id=${m.id}`)}
                      style={{display:"flex",alignItems:"center",gap:5,padding:"5px 10px",borderRadius:8,border:"1px solid rgba(8,145,178,.22)",background:"rgba(8,145,178,.07)",cursor:"pointer",fontSize:11,fontWeight:600,color:"#0891b2",fontFamily:"var(--f)",transition:"all .14s",whiteSpace:"nowrap"}}
                      onMouseEnter={e=>{e.currentTarget.style.background="rgba(8,145,178,.15)";}}
                      onMouseLeave={e=>{e.currentTarget.style.background="rgba(8,145,178,.07)";}}>
                      ✏️ Edit
                    </button>
                    <button title="Remove member"
                      onClick={()=>setRemoveModal({ id: m.id, name: m.name || "this member", isRequested: !!m.is_user_requested })}
                      style={{width:30,height:30,borderRadius:8,border:"1px solid rgba(220,38,38,.2)",background:"rgba(220,38,38,.07)",cursor:"pointer",fontSize:13,color:"#dc2626",display:"flex",alignItems:"center",justifyContent:"center",transition:"all .14s"}}
                      onMouseEnter={e=>{e.currentTarget.style.background="rgba(220,38,38,.15)";}}
                      onMouseLeave={e=>{e.currentTarget.style.background="rgba(220,38,38,.07)";}}>✕</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>

      {/* Pagination */}
      {last_page > 1 && (
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:16,padding:"0 4px"}}>
          <div style={{fontSize:12,color:"var(--t3)"}}>
            Showing page {current_page} of {last_page} · {total} members
          </div>
          <div style={{display:"flex",alignItems:"center",gap:4}}>
            <button
              disabled={current_page <= 1}
              onClick={() => setPage(p => p - 1)}
              style={{padding:"6px 12px",borderRadius:8,border:"1px solid rgba(0,0,0,.1)",background:current_page<=1?"rgba(0,0,0,.03)":"rgba(255,255,255,.8)",cursor:current_page<=1?"not-allowed":"pointer",fontSize:12,fontWeight:600,color:current_page<=1?"var(--t4)":"var(--t2)",fontFamily:"var(--f)",transition:"all .14s"}}>
              ← Prev
            </button>
            {pages.map((p, i) =>
              p === '...' ? (
                <span key={`dot-${i}`} style={{padding:"0 4px",color:"var(--t4)",fontSize:12}}>…</span>
              ) : (
                <button key={p} onClick={() => setPage(p)}
                  style={{
                    minWidth:32,height:32,borderRadius:8,
                    border: p === current_page ? "1px solid rgba(100,116,139,.4)" : "1px solid rgba(0,0,0,.08)",
                    background: p === current_page ? "rgba(100,116,139,.12)" : "rgba(255,255,255,.8)",
                    cursor:"pointer",fontSize:12,fontWeight: p === current_page ? 700 : 500,
                    color: p === current_page ? "var(--t1)" : "var(--t2)",
                    fontFamily:"var(--f)",transition:"all .14s",
                    display:"flex",alignItems:"center",justifyContent:"center",
                  }}
                  onMouseEnter={e=>{if(p!==current_page)e.currentTarget.style.background="rgba(100,116,139,.08)";}}
                  onMouseLeave={e=>{if(p!==current_page)e.currentTarget.style.background="rgba(255,255,255,.8)";}}>
                  {p}
                </button>
              )
            )}
            <button
              disabled={current_page >= last_page}
              onClick={() => setPage(p => p + 1)}
              style={{padding:"6px 12px",borderRadius:8,border:"1px solid rgba(0,0,0,.1)",background:current_page>=last_page?"rgba(0,0,0,.03)":"rgba(255,255,255,.8)",cursor:current_page>=last_page?"not-allowed":"pointer",fontSize:12,fontWeight:600,color:current_page>=last_page?"var(--t4)":"var(--t2)",fontFamily:"var(--f)",transition:"all .14s"}}>
              Next →
            </button>
          </div>
        </div>
      )}

      {/* Remove Confirmation Modal */}
      {removeModal && createPortal(
        <div style={{position:"fixed",inset:0,zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(15,23,42,.45)",backdropFilter:"blur(6px)"}}
          onClick={e=>{if(e.target===e.currentTarget) setRemoveModal(null);}}>
          <div style={{width:"100%",maxWidth:420,background:"rgba(255,255,255,.97)",backdropFilter:"blur(40px)",borderRadius:20,boxShadow:"0 32px 80px rgba(0,0,0,.18)",padding:"28px 28px 24px",animation:"fadeIn .2s ease"}}>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:18}}>
              <div style={{width:44,height:44,borderRadius:12,background:"rgba(220,38,38,.1)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>⚠️</div>
              <div>
                <div style={{fontSize:16,fontWeight:720,color:"#0f172a"}}>
                  {removeModal.isRequested ? "Disconnect Member?" : "Delete Member?"}
                </div>
                <div style={{fontSize:12,color:"#64748b",marginTop:2}}>
                  {removeModal.isRequested
                    ? `"${removeModal.name}" joined via request. This will disconnect them from your enterprise.`
                    : `"${removeModal.name}" was created by your enterprise. This will permanently delete their profile and all associated data.`
                  }
                </div>
              </div>
            </div>

            <div style={{padding:"12px 14px",background:"rgba(220,38,38,.06)",border:"1px solid rgba(220,38,38,.15)",borderRadius:10,marginBottom:18}}>
              <div style={{fontSize:12,color:"#dc2626",fontWeight:600,display:"flex",alignItems:"center",gap:6}}>
                <span>⛔</span> This action cannot be undone
              </div>
              <div style={{fontSize:11.5,color:"#64748b",marginTop:4}}>
                {removeModal.isRequested
                  ? "The member will lose access to enterprise features but their personal profile will remain."
                  : "All profile data, links, leads, connections, and cards associated with this member will be permanently removed."
                }
              </div>
            </div>

            <div style={{display:"flex",gap:9,justifyContent:"flex-end"}}>
              <button className="btn bs" style={{padding:"9px 20px",fontSize:13}} onClick={()=>setRemoveModal(null)}>Cancel</button>
              <button style={{padding:"9px 20px",borderRadius:10,border:"none",background:removeModal.isRequested?"#d97706":"#dc2626",color:"#fff",cursor:"pointer",fontSize:13,fontWeight:650,fontFamily:"var(--f)",transition:"all .15s",boxShadow:"0 2px 8px rgba(220,38,38,.3)"}}
                onMouseEnter={e=>e.currentTarget.style.opacity=".9"}
                onMouseLeave={e=>e.currentTarget.style.opacity="1"}
                onClick={handleRemoveConfirm}>
                {removeModal.isRequested ? "Disconnect" : "Delete Permanently"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
});
